/**
 * Browser-compatible PostgreSQL search service
 * 
 * This is a browser-friendly implementation that either:
 * 1. Delegates to the backend API for searches
 * 2. Falls back to Firebase client SDK when appropriate
 */
import postgresDb from './db-browser';
import logger from '../../utils/logger';

class PostgresSearch {
  private isInitialized: boolean = false;

  /**
   * Initialize the PostgreSQL search service
   */
  public async initialize(): Promise<boolean> {
    try {
      this.isInitialized = await postgresDb.initialize();
      return this.isInitialized;
    } catch (error) {
      logger.error('Error initializing PostgreSQL search service', error);
      this.isInitialized = false;
      return false;
    }
  }

  /**
   * Check if the PostgreSQL search service is healthy
   */
  public async isHealthy(): Promise<boolean> {
    try {
      return await postgresDb.isHealthy();
    } catch (error) {
      return false;
    }
  }

  /**
   * Index a user for search (in browser, this is a no-op)
   */
  public async indexUser(userData: any): Promise<boolean> {
    logger.info('Indexing user data is not supported in browser environment');
    return false;
  }

  /**
   * Index a technique for search (in browser, this is a no-op)
   */
  public async indexTechnique(techniqueData: any): Promise<boolean> {
    logger.info('Indexing technique data is not supported in browser environment');
    return false;
  }

  /**
   * Index a training for search (in browser, this is a no-op)
   */
  public async indexTraining(trainingData: any): Promise<boolean> {
    logger.info('Indexing training data is not supported in browser environment');
    return false;
  }

  /**
   * Index a post for search (in browser, this is a no-op)
   */
  public async indexPost(postData: any): Promise<boolean> {
    logger.info('Indexing post data is not supported in browser environment');
    return false;
  }

  /**
   * Delete indexed data for an item (in browser, this is a no-op)
   */
  public async deleteIndexedItem(id: string, type: 'user' | 'technique' | 'training' | 'post'): Promise<boolean> {
    logger.info(`Deleting indexed data is not supported in browser environment`);
    return false;
  }

  /**
   * Search for users
   */
  public async searchUsers(query: string, limit = 10, offset = 0): Promise<any[]> {
    try {
      // Check PostgreSQL health
      if (!await this.isHealthy()) {
        logger.error('PostgreSQL search service is not healthy');
        return [];
      }
      
      // Execute PostgreSQL search through the API
      return await postgresDb.searchUsers(query, limit, offset);
    } catch (error) {
      logger.error('Error searching users in PostgreSQL', error);
      return [];
    }
  }

  /**
   * Search for techniques
   */
  public async searchTechniques(query: string, limit = 10, offset = 0): Promise<any[]> {
    try {
      // Check PostgreSQL health
      if (!await this.isHealthy()) {
        logger.error('PostgreSQL search service is not healthy');
        return [];
      }
      
      // Execute PostgreSQL search through the API
      return await postgresDb.searchTechniques(query, limit, offset);
    } catch (error) {
      logger.error('Error searching techniques in PostgreSQL', error);
      return [];
    }
  }

  /**
   * Search for trainings
   */
  public async searchTrainings(query: string, limit = 10, offset = 0): Promise<any[]> {
    try {
      // Check PostgreSQL health
      if (!await this.isHealthy()) {
        logger.error('PostgreSQL search service is not healthy');
        return [];
      }
      
      // Execute PostgreSQL search through the API
      return await postgresDb.searchTrainings(query, limit, offset);
    } catch (error) {
      logger.error('Error searching trainings in PostgreSQL', error);
      return [];
    }
  }

  /**
   * Search for posts
   */
  public async searchPosts(query: string, limit = 10, offset = 0): Promise<any[]> {
    try {
      // Check PostgreSQL health
      if (!await this.isHealthy()) {
        logger.error('PostgreSQL search service is not healthy');
        return [];
      }
      
      // Execute PostgreSQL search through the API
      return await postgresDb.searchPosts(query, limit, offset);
    } catch (error) {
      logger.error('Error searching posts in PostgreSQL', error);
      return [];
    }
  }

  /**
   * Search for workout routines
   */
  public async searchWorkoutRoutines(query: string, limit = 10, offset = 0): Promise<any[]> {
    try {
      // Check PostgreSQL health
      if (!await this.isHealthy()) {
        logger.error('PostgreSQL search service is not healthy');
        return [];
      }
      
      // Execute PostgreSQL search through the API
      return await postgresDb.searchWorkoutRoutines(query, limit, offset);
    } catch (error) {
      logger.error('Error searching workout routines in PostgreSQL', error);
      return [];
    }
  }
}

export const postgresSearch = new PostgresSearch();
export default postgresSearch;